import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowRight, CheckCircle2, X, Phone, Calendar, Users, MessageSquare, Target, TrendingUp, RefreshCw, Clock, Shield, Zap } from 'lucide-react';
import ApexNavbar from '../components/apex/ApexNavbar';
import ApexFooter from '../components/apex/ApexFooter';

const CALENDLY_URL = 'https://calendly.com/kyran-apexfitsalescoaching/30min';
const API_URL = process.env.REACT_APP_BACKEND_URL;

const FiveKGuarantee = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    businessName: '',
    email: '',
    phone: '',
    instagram: '',
    businessType: '',
    monthlyRevenue: '',
    monthlyLeads: '',
    monthlyConsultations: '',
    closeRate: '',
    avgCustomerValue: '',
    biggestChallenge: '',
    whyFiveK: '',
    preparedToInvest: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Handle hash navigation to scroll to application section
  useEffect(() => {
    if (location.hash === '#application') {
      // Small delay to ensure the page has rendered
      setTimeout(() => {
        const element = document.getElementById('application');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  }, [location]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Submit to backend API
      const response = await fetch(`${API_URL}/api/applications/5k-guarantee`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      if (!response.ok) {
        throw new Error('Failed to submit application');
      }
      
      setIsSubmitted(true);
      
      // Scroll to the top of application section to show success message
      setTimeout(() => {
        const element = document.getElementById('application');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
      
      // Track conversion event for Meta Pixel
      if (window.fbq) {
        window.fbq('track', 'Lead', { content_name: '5K Guarantee Application' });
      }
    } catch {
      // Still show success to user - form data was at least attempted
      setIsSubmitted(true);
      
      // Scroll to the top of application section to show success message
      setTimeout(() => {
        const element = document.getElementById('application');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
      
      if (window.fbq) {
        window.fbq('track', 'Lead', { content_name: '5K Guarantee Application' });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const openCalendly = () => {
    if (window.Calendly) {
      window.Calendly.initPopupWidget({ url: CALENDLY_URL });
      
      // Listen for Calendly event completion
      window.addEventListener('message', function calendlyListener(e) {
        if (e.data.event && e.data.event === 'calendly.event_scheduled') {
          window.removeEventListener('message', calendlyListener);
          // Track Calendly booking for Meta Pixel
          if (window.fbq) {
            window.fbq('track', 'Schedule', { content_name: 'Sales Audit Booking' });
          }
          // Redirect to thank you page
          navigate('/thank-you');
        }
      });
    } else {
      window.open(CALENDLY_URL, '_blank');
    }
  };

  const scrollToApplication = () => {
    document.getElementById('application')?.scrollIntoView({ behavior: 'smooth' });
    
    // Track application started for Meta Pixel
    if (window.fbq) {
      window.fbq('track', 'InitiateCheckout', { content_name: '5K Guarantee Application Started' });
    }
  };

  // Revenue leak stages
  const leakStages = [
    { label: 'LEAD', icon: Users },
    { label: 'CONTACT', icon: Phone },
    { label: 'APPOINTMENT', icon: Calendar },
    { label: 'SHOW', icon: CheckCircle2 },
    { label: 'CONSULTATION', icon: MessageSquare },
    { label: 'SALE', icon: Target },
    { label: 'FOLLOW-UP', icon: RefreshCw }
  ];

  // Where we find the £5K cards
  const hidingSpots = [
    {
      id: 'uncontacted',
      icon: Users,
      title: 'UNCONTACTED LEADS',
      description: 'Leads you have paid for that we will convert into paying members.'
    },
    {
      id: 'noshows',
      icon: X,
      title: 'NO-SHOWS',
      description: 'People who booked but disappeared — we will bring them back.'
    },
    {
      id: 'unconverted',
      icon: MessageSquare,
      title: 'UNCONVERTED CONSULTATIONS',
      description: 'People who sat in front of you and did not buy — we will close them.'
    },
    {
      id: 'oldleads',
      icon: Clock,
      title: 'OLD LEADS',
      description: 'People who enquired months ago — we will reactivate them.'
    },
    {
      id: 'pastclients',
      icon: RefreshCw,
      title: 'PAST CLIENTS',
      description: 'Previous members who left — we will bring them back. This is your goldmine.'
    },
    {
      id: 'followup',
      icon: TrendingUp,
      title: 'FOLLOW-UP',
      description: 'People who said "I will think about it" — we will get them over the line.'
    },
    {
      id: 'process',
      icon: Target,
      title: 'YOUR SALES PROCESS',
      description: 'The small mistakes costing you thousands — we will fix them.'
    }
  ];

  // Timeline steps
  const timelineSteps = [
    {
      number: '01',
      title: 'AUDIT',
      description: 'We analyse your sales process, lead flow, conversion rates and revenue.'
    },
    {
      number: '02',
      title: 'IDENTIFY',
      description: 'We pinpoint exactly where your £5K is sitting — and how to extract it.'
    },
    {
      number: '03',
      title: 'IMPLEMENT',
      description: 'We work with you 1-to-1 to fix your sales process and recover lost revenue.'
    },
    {
      number: '04',
      title: 'OPTIMISE',
      description: 'We track every number and maximise return from every opportunity.'
    },
    {
      number: '05',
      title: '£5K',
      description: 'You collect an additional £5,000 in revenue within 30 days. Guaranteed.'
    }
  ];

  // Who this is for
  const idealCharacteristics = [
    'Gym or studio owner',
    'Already generating leads',
    'Already selling memberships/programmes/PT',
    'Has an existing sales process',
    'Wants to improve conversion',
    'Willing to implement',
    'Prepared to be held accountable'
  ];

  // Programme inclusions
  const programmeInclusions = [
    'Full sales-process audit',
    'Lead conversion analysis',
    'Consultation review',
    'Follow-up system',
    'Objection handling',
    'Sales process optimisation',
    'Reactivation strategy',
    'Weekly 1-to-1 coaching',
    'Sales KPI tracking',
    'Direct implementation support'
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <ApexNavbar />
      
      <main>
        {/* HERO SECTION */}
        <section className="relative min-h-screen flex items-center pt-20 overflow-hidden">
          {/* Background effects */}
          <div className="absolute inset-0 bg-gradient-to-br from-[#0A0A0A] via-[#0A0A0A] to-[#0d1f12]" />
          <div className="absolute top-1/4 right-1/4 w-[600px] h-[600px] bg-[#22C55E]/10 rounded-full blur-[180px]" />
          
          <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-12 py-20 text-center">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#22C55E]/30 bg-[#22C55E]/10 mb-8">
              <span className="text-[#22C55E] text-sm font-bold tracking-wider uppercase">
                The Apex £5K Sales Guarantee
              </span>
            </div>

            {/* Main headline */}
            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold text-white leading-[1.1] tracking-tight mb-6">
              WE WILL FIND{' '}
              <span className="text-[#22C55E]">£5,000</span>{' '}
              OF EXTRA REVENUE IN YOUR BUSINESS.
            </h1>

            {/* Body copy */}
            <p className="text-lg md:text-xl text-[#A1A1AA] leading-relaxed max-w-3xl mx-auto mb-4">
              We work 1-to-1 with gym and studio owners to identify and fix the sales leaks costing them thousands.
            </p>
            <p className="text-lg md:text-xl text-white font-semibold max-w-3xl mx-auto mb-2">
              Generate an additional £5,000 in collected revenue within 30 days — or we refund your coaching fee.*
            </p>
            <p className="text-sm text-[#71717A] mb-10">
              *Subject to qualification and agreed programme conditions.
            </p>

            {/* CTA */}
            <button
              onClick={scrollToApplication}
              data-testid="hero-apply-btn"
              className="group inline-flex items-center justify-center gap-3 bg-[#22C55E] hover:bg-[#16A34A] text-black font-bold uppercase tracking-wide rounded-full px-10 py-5 text-lg transition-all duration-300 hover:-translate-y-1 shadow-lg shadow-[#22C55E]/25"
            >
              Apply For The £5K Guarantee
              <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
            </button>

            <p className="text-[#71717A] text-sm mt-6 max-w-lg mx-auto">
              We assess your numbers before accepting you onto the guarantee. Not every business qualifies — but if yours does, we will deliver.
            </p>
          </div>
        </section>

        {/* SECTION 2 — THE PROBLEM */}
        <section className="relative py-20 md:py-28 bg-[#0d0d0d]">
          <div className="max-w-6xl mx-auto px-6 md:px-12">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-5xl font-bold text-white mb-4">
                YOU DO NOT HAVE A LEAD PROBLEM.
              </h2>
              <p className="text-2xl md:text-3xl font-bold text-[#22C55E]">
                YOU HAVE A CONVERSION PROBLEM. WE FIX IT.
              </p>
            </div>

            {/* Revenue leak flow */}
            <div className="mb-16">
              <div className="flex flex-wrap justify-center items-center gap-2 md:gap-4">
                {leakStages.map((stage, index) => (
                  <React.Fragment key={stage.label}>
                    <div className="flex flex-col items-center">
                      <div className="w-16 h-16 md:w-20 md:h-20 rounded-xl bg-[#121212] border border-[#27272A] flex items-center justify-center mb-2 hover:border-red-500/50 transition-colors group">
                        <stage.icon className="w-6 h-6 md:w-8 md:h-8 text-[#A1A1AA] group-hover:text-red-400 transition-colors" />
                      </div>
                      <span className="text-[10px] md:text-xs text-[#71717A] font-medium">{stage.label}</span>
                    </div>
                    {index < leakStages.length - 1 && (
                      <div className="hidden md:flex items-center">
                        <ArrowRight className="w-5 h-5 text-red-500/50" />
                        <div className="w-2 h-2 rounded-full bg-red-500/30 animate-pulse" />
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>
              <p className="text-center text-red-400 text-sm mt-4 font-medium">
                ↑ Revenue leaks at every stage ↑
              </p>
            </div>

            {/* Explanation */}
            <div className="max-w-3xl mx-auto bg-[#121212] border border-[#27272A] rounded-2xl p-8 md:p-10">
              <p className="text-[#A1A1AA] text-lg leading-relaxed mb-6">
                Imagine you are generating <span className="text-white font-semibold">100 leads every month</span>.
              </p>
              <ul className="space-y-3 text-[#A1A1AA] mb-6">
                <li className="flex items-start gap-3">
                  <span className="text-red-400 font-bold">-20%</span>
                  <span>because your follow-up is poor</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-red-400 font-bold">-20%</span>
                  <span>because people are not booking</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-red-400 font-bold">-??%</span>
                  <span>because your consultations are not converting</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-red-400 font-bold">-??%</span>
                  <span>because you barely follow up with people who did not buy</span>
                </li>
              </ul>
              <p className="text-xl md:text-2xl text-white font-bold text-center">
                Your £5K is already sitting inside your existing database. We will extract it.
              </p>
            </div>
          </div>
        </section>

        {/* SECTION 3 — WHERE WE FIND THE £5K */}
        <section className="relative py-20 md:py-28 bg-[#0A0A0A]">
          <div className="max-w-6xl mx-auto px-6 md:px-12">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-5xl font-bold text-white">
                HERE IS WHERE WE FIND YOUR <span className="text-[#22C55E]">£5K</span>
              </h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {hidingSpots.map((spot) => (
                <div
                  key={spot.id}
                  data-testid={`hiding-${spot.id}`}
                  className="group bg-[#121212] border border-[#27272A] rounded-2xl p-6 hover:border-[#22C55E]/50 hover:-translate-y-1 transition-all duration-300"
                >
                  <div className="w-12 h-12 rounded-xl bg-[#22C55E]/10 flex items-center justify-center mb-4 group-hover:bg-[#22C55E]/20 transition-colors">
                    <spot.icon className="w-6 h-6 text-[#22C55E]" />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2">{spot.title}</h3>
                  <p className="text-[#A1A1AA] text-sm">{spot.description}</p>
                </div>
              ))}
            </div>

            {/* Reactivation highlight */}
            <div className="mt-12 bg-gradient-to-r from-[#121212] via-[#0d1f12] to-[#121212] border border-[#22C55E]/30 rounded-2xl p-8 md:p-10">
              <div className="flex flex-col lg:flex-row items-center gap-8">
                <div className="w-16 h-16 rounded-full bg-[#22C55E]/20 flex items-center justify-center flex-shrink-0">
                  <RefreshCw className="w-8 h-8 text-[#22C55E]" />
                </div>
                <div className="text-center lg:text-left">
                  <h3 className="text-xl md:text-2xl font-bold text-white mb-3">
                    YOUR PAST CLIENTS ARE A GOLDMINE — WE WILL MINE IT
                  </h3>
                  <p className="text-[#A1A1AA] leading-relaxed">
                    A large portion of your £5K is sitting in your <span className="text-white font-semibold">previous client database</span>. 
                    People who already know you, trust you, and have paid you before. They left for various reasons — life got busy, 
                    they moved, circumstances changed. With the right reactivation campaign, many of them will come back. 
                    <span className="text-[#22C55E] font-semibold"> We make it happen.</span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 4 — THE APEX APPROACH */}
        <section className="relative py-20 md:py-28 bg-[#0d0d0d]">
          <div className="max-w-5xl mx-auto px-6 md:px-12">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-white leading-tight">
                WE DO NOT JUST TELL YOU WHAT TO DO.
                <br />
                <span className="text-[#22C55E]">WE DO IT WITH YOU.</span>
              </h2>
            </div>

            <div className="bg-[#121212] border border-[#27272A] rounded-2xl p-8 md:p-12">
              <div className="space-y-4 text-lg text-[#A1A1AA] mb-8">
                <p className="flex items-center gap-3">
                  <X className="w-5 h-5 text-red-400 flex-shrink-0" />
                  <span>No generic PowerPoint presentation.</span>
                </p>
                <p className="flex items-center gap-3">
                  <X className="w-5 h-5 text-red-400 flex-shrink-0" />
                  <span>No huge group coaching call.</span>
                </p>
                <p className="flex items-center gap-3">
                  <X className="w-5 h-5 text-red-400 flex-shrink-0" />
                  <span>No &ldquo;here is what you should do — good luck implementing it.&rdquo;</span>
                </p>
              </div>

              <div className="border-t border-[#27272A] pt-8">
                <p className="text-xl text-white font-semibold mb-6">
                  We work directly with you inside your actual business.
                </p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {['Your leads.', 'Your numbers.', 'Your consultations.', 'Your follow-up.', 'Your sales process.', 'Your revenue.'].map((item) => (
                    <p key={item} className="text-[#22C55E] font-medium">{item}</p>
                  ))}
                </div>
                <p className="text-xl text-white font-bold mt-8">
                  We find what is costing you money — and we fix it.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 5 — HOW THE £5K GUARANTEE WORKS */}
        <section className="relative py-20 md:py-28 bg-[#0A0A0A]">
          <div className="max-w-6xl mx-auto px-6 md:px-12">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-5xl font-bold text-white">
                30 DAYS. ONE TARGET. <span className="text-[#22C55E]">£5,000. GUARANTEED.</span>
              </h2>
            </div>

            {/* Timeline */}
            <div className="relative">
              {/* Connection line */}
              <div className="hidden lg:block absolute top-16 left-[10%] right-[10%] h-0.5 bg-gradient-to-r from-[#22C55E]/30 via-[#22C55E] to-[#22C55E]/30" />
              
              <div className="grid md:grid-cols-5 gap-8">
                {timelineSteps.map((step, index) => (
                  <div key={step.number} className="text-center relative">
                    <div className="w-16 h-16 rounded-full bg-[#22C55E] flex items-center justify-center mx-auto mb-4 relative z-10">
                      <span className="text-black font-bold text-xl">{step.number}</span>
                    </div>
                    <h3 className="text-lg font-bold text-white mb-2">{step.title}</h3>
                    <p className="text-[#A1A1AA] text-sm">{step.description}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Guarantee statement */}
            <div className="mt-16 text-center">
              <div className="inline-block bg-[#22C55E]/10 border border-[#22C55E]/30 rounded-2xl px-8 py-6">
                <p className="text-2xl md:text-3xl font-bold text-white mb-2">
                  WE HIT £5K — OR YOU DO NOT PAY.
                </p>
                <p className="text-sm text-[#71717A]">
                  *Subject to qualification, agreed programme conditions and completion of required actions.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 6 — WHO THIS IS FOR */}
        <section className="relative py-20 md:py-28 bg-[#0d0d0d]">
          <div className="max-w-5xl mx-auto px-6 md:px-12">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-5xl font-bold text-white">
                WE DO NOT WORK WITH EVERY GYM.
              </h2>
            </div>

            <div className="grid lg:grid-cols-2 gap-8">
              {/* Explanation */}
              <div className="bg-[#121212] border border-[#27272A] rounded-2xl p-8">
                <p className="text-[#A1A1AA] text-lg leading-relaxed mb-6">
                  We do not accept every business onto the guarantee.
                </p>
                <p className="text-[#A1A1AA] text-lg leading-relaxed mb-6">
                  Before you start, we analyse your numbers and determine whether there is enough opportunity to generate an additional £5,000 within 30 days.
                </p>
                <p className="text-white text-lg font-semibold">
                  If we cannot do it, we will tell you. If we can — we will deliver.
                </p>
              </div>

              {/* Ideal characteristics */}
              <div className="bg-[#121212] border border-[#22C55E]/30 rounded-2xl p-8">
                <h3 className="text-lg font-bold text-white mb-6">IDEAL FOR:</h3>
                <ul className="space-y-3 mb-8">
                  {idealCharacteristics.map((item) => (
                    <li key={item} className="flex items-center gap-3 text-white">
                      <CheckCircle2 className="w-5 h-5 text-[#22C55E] flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
                <div className="border-t border-[#27272A] pt-6">
                  <p className="text-[#A1A1AA] text-sm">
                    No sales infrastructure yet? No problem. We build it with you from scratch.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 7 — CREDIBILITY */}
        <section className="relative py-20 md:py-28 bg-[#0A0A0A]">
          <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              <span className="text-[#22C55E]">8,000+</span> CONSULTATIONS.{' '}
              <span className="text-[#22C55E]">7+ FIGURES</span> IN FITNESS SALES.{' '}
              <span className="text-[#22C55E]">10 YEARS</span> IN THE INDUSTRY.
            </h2>
            <p className="text-lg text-[#A1A1AA] leading-relaxed max-w-3xl mx-auto mt-8">
              I have spent the last decade in fitness sales — more than 8,000 consultations and well over 7 figures in programme and membership sales.
            </p>
            <p className="text-lg text-white font-semibold mt-6">
              Apex takes that experience and puts it directly into your business. We know what works because we have done it thousands of times.
            </p>
          </div>
        </section>

        {/* SECTION 8 — THE OFFER */}
        <section className="relative py-20 md:py-28 bg-[#0d0d0d]">
          <div className="max-w-4xl mx-auto px-6 md:px-12">
            <div className="text-center mb-12">
              <span className="text-[#22C55E] text-sm font-medium tracking-[0.2em] uppercase">
                The Programme
              </span>
              <h2 className="text-3xl md:text-5xl font-bold text-white mt-4">
                THE APEX £5K SALES GUARANTEE
              </h2>
            </div>

            <div className="bg-gradient-to-br from-[#121212] to-[#0d1f12] border border-[#22C55E]/30 rounded-3xl p-8 md:p-12">
              <h3 className="text-xl font-bold text-white mb-6 text-center">
                30 DAYS OF 1-TO-1 SALES COACHING
              </h3>

              <div className="grid md:grid-cols-2 gap-4 mb-10">
                {programmeInclusions.map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[#22C55E] flex-shrink-0" />
                    <span className="text-white">{item}</span>
                  </div>
                ))}
              </div>

              <div className="border-t border-[#27272A] pt-8 text-center">
                <p className="text-sm text-[#22C55E] font-medium tracking-wider uppercase mb-2">
                  YOUR TARGET
                </p>
                <p className="text-4xl md:text-5xl font-bold text-white mb-4">
                  +£5,000 COLLECTED REVENUE
                </p>
                <p className="text-xl text-[#22C55E] font-bold">
                  WE HIT IT — OR YOU DO NOT PAY.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 9 — APPLICATION */}
        <section id="application" className="relative py-20 md:py-28 bg-[#0A0A0A]">
          <div className="max-w-3xl mx-auto px-6 md:px-12">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                READY TO ADD <span className="text-[#22C55E]">£5K</span> TO YOUR REVENUE?
              </h2>
              <p className="text-[#A1A1AA] text-lg">
                Tell us about your business. If you qualify, we will find your £5K.
              </p>
            </div>

            {!isSubmitted ? (
              <form onSubmit={handleSubmit} className="bg-[#121212] border border-[#27272A] rounded-2xl p-8 space-y-6">
                {/* Personal Info */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-white text-sm font-medium mb-2">Name *</label>
                    <input
                      type="text"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg px-4 py-3 text-white focus:border-[#22C55E] focus:outline-none transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-white text-sm font-medium mb-2">Business Name *</label>
                    <input
                      type="text"
                      name="businessName"
                      required
                      value={formData.businessName}
                      onChange={handleInputChange}
                      className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg px-4 py-3 text-white focus:border-[#22C55E] focus:outline-none transition-colors"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-white text-sm font-medium mb-2">Email *</label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg px-4 py-3 text-white focus:border-[#22C55E] focus:outline-none transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-white text-sm font-medium mb-2">Phone *</label>
                    <input
                      type="tel"
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg px-4 py-3 text-white focus:border-[#22C55E] focus:outline-none transition-colors"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-white text-sm font-medium mb-2">Instagram / Website</label>
                    <input
                      type="text"
                      name="instagram"
                      value={formData.instagram}
                      onChange={handleInputChange}
                      className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg px-4 py-3 text-white focus:border-[#22C55E] focus:outline-none transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-white text-sm font-medium mb-2">Business Type *</label>
                    <select
                      name="businessType"
                      required
                      value={formData.businessType}
                      onChange={handleInputChange}
                      className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg px-4 py-3 text-white focus:border-[#22C55E] focus:outline-none transition-colors"
                    >
                      <option value="">Select...</option>
                      <option value="gym">Gym</option>
                      <option value="studio">Studio</option>
                      <option value="pt">Personal Training</option>
                      <option value="crossfit">CrossFit Box</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>

                {/* Business Numbers */}
                <div className="border-t border-[#27272A] pt-6">
                  <h3 className="text-white font-semibold mb-4">Your Numbers</h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-white text-sm font-medium mb-2">Approximate Monthly Revenue</label>
                      <input
                        type="text"
                        name="monthlyRevenue"
                        value={formData.monthlyRevenue}
                        onChange={handleInputChange}
                        placeholder="e.g. £15,000"
                        className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg px-4 py-3 text-white focus:border-[#22C55E] focus:outline-none transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-white text-sm font-medium mb-2">Approximate Monthly Leads</label>
                      <input
                        type="text"
                        name="monthlyLeads"
                        value={formData.monthlyLeads}
                        onChange={handleInputChange}
                        placeholder="e.g. 50"
                        className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg px-4 py-3 text-white focus:border-[#22C55E] focus:outline-none transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-white text-sm font-medium mb-2">Approximate Monthly Consultations</label>
                      <input
                        type="text"
                        name="monthlyConsultations"
                        value={formData.monthlyConsultations}
                        onChange={handleInputChange}
                        placeholder="e.g. 20"
                        className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg px-4 py-3 text-white focus:border-[#22C55E] focus:outline-none transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-white text-sm font-medium mb-2">Approximate Close Rate</label>
                      <input
                        type="text"
                        name="closeRate"
                        value={formData.closeRate}
                        onChange={handleInputChange}
                        placeholder="e.g. 30%"
                        className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg px-4 py-3 text-white focus:border-[#22C55E] focus:outline-none transition-colors"
                      />
                    </div>
                  </div>
                  <div className="mt-6">
                    <label className="block text-white text-sm font-medium mb-2">Average Customer Value</label>
                    <input
                      type="text"
                      name="avgCustomerValue"
                      value={formData.avgCustomerValue}
                      onChange={handleInputChange}
                      placeholder="e.g. £150/month"
                      className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg px-4 py-3 text-white focus:border-[#22C55E] focus:outline-none transition-colors"
                    />
                  </div>
                </div>

                {/* Challenges */}
                <div className="border-t border-[#27272A] pt-6">
                  <div className="mb-6">
                    <label className="block text-white text-sm font-medium mb-2">Biggest Current Sales Challenge *</label>
                    <textarea
                      name="biggestChallenge"
                      required
                      value={formData.biggestChallenge}
                      onChange={handleInputChange}
                      rows={3}
                      className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg px-4 py-3 text-white focus:border-[#22C55E] focus:outline-none transition-colors resize-none"
                    />
                  </div>
                  <div>
                    <label className="block text-white text-sm font-medium mb-2">Why do you believe your business could generate an additional £5,000? *</label>
                    <textarea
                      name="whyFiveK"
                      required
                      value={formData.whyFiveK}
                      onChange={handleInputChange}
                      rows={3}
                      className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg px-4 py-3 text-white focus:border-[#22C55E] focus:outline-none transition-colors resize-none"
                    />
                  </div>
                </div>

                {/* Qualification question */}
                <div className="border-t border-[#27272A] pt-6">
                  <label className="block text-white text-sm font-medium mb-4">
                    If we determine that you are a suitable candidate for the £5K Guarantee, are you prepared to invest in the programme? *
                  </label>
                  <div className="space-y-3">
                    {['Yes', 'Possibly, depending on the Sales Audit', 'No'].map((option) => (
                      <label key={option} className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="radio"
                          name="preparedToInvest"
                          value={option}
                          checked={formData.preparedToInvest === option}
                          onChange={handleInputChange}
                          required
                          className="w-4 h-4 accent-[#22C55E]"
                        />
                        <span className="text-[#A1A1AA]">{option}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  data-testid="submit-application"
                  className="w-full bg-[#22C55E] hover:bg-[#16A34A] text-black font-bold uppercase tracking-wide rounded-full px-8 py-4 transition-all duration-300 disabled:opacity-50"
                >
                  {isSubmitting ? 'Submitting...' : 'Apply For The £5K Guarantee'}
                </button>
              </form>
            ) : (
              /* Thank you state - prominently displayed */
              <div className="bg-gradient-to-br from-[#121212] to-[#0d1f12] border-2 border-[#22C55E] rounded-2xl p-8 md:p-10 text-center shadow-2xl shadow-[#22C55E]/20">
                <div className="w-20 h-20 rounded-full bg-[#22C55E]/20 flex items-center justify-center mx-auto mb-6 animate-pulse">
                  <CheckCircle2 className="w-10 h-10 text-[#22C55E]" />
                </div>
                <h3 className="text-3xl font-bold text-white mb-4">Application Received!</h3>
                <p className="text-lg text-[#A1A1AA] mb-8 max-w-md mx-auto">
                  Thank you for your application. The next step is to book your Sales Audit so we can review your numbers.
                </p>

                {/* Calendly booking area - highly visible */}
                <div className="bg-[#0A0A0A] rounded-xl p-8 border-2 border-[#22C55E]/50">
                  <div className="flex items-center justify-center gap-2 mb-4">
                    <div className="w-3 h-3 rounded-full bg-[#22C55E] animate-pulse"></div>
                    <span className="text-[#22C55E] text-sm font-bold uppercase tracking-wider">Action Required</span>
                  </div>
                  <h4 className="text-2xl font-bold text-white mb-3">
                    BOOK YOUR 30-MINUTE SALES AUDIT
                  </h4>
                  <p className="text-[#A1A1AA] mb-8 max-w-sm mx-auto">
                    Click below to schedule your call. We will review your numbers and determine if you qualify for the £5K Guarantee.
                  </p>
                  <button
                    onClick={openCalendly}
                    data-testid="book-audit-btn"
                    className="inline-flex items-center justify-center gap-3 bg-[#22C55E] hover:bg-[#16A34A] text-black font-bold uppercase tracking-wide rounded-full px-10 py-5 text-lg transition-all duration-300 hover:-translate-y-1 shadow-lg shadow-[#22C55E]/30"
                  >
                    <Calendar className="w-6 h-6" />
                    Book Your Sales Audit Now
                  </button>
                  <p className="text-[#71717A] text-xs mt-4">
                    Limited availability — book now to secure your spot
                  </p>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="relative py-20 md:py-28 bg-[#0d0d0d]">
          <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-4">
              YOU DO NOT NEED MORE LEADS.
            </h2>
            <p className="text-2xl md:text-3xl font-bold text-[#A1A1AA] mb-8">
              YOU NEED TO CONVERT THE ONES YOU ALREADY HAVE. WE MAKE IT HAPPEN.
            </p>
            <p className="text-lg text-[#22C55E] font-semibold mb-10">
              £5,000 in additional revenue in 30 days — guaranteed.
            </p>
            <button
              onClick={scrollToApplication}
              data-testid="final-cta-btn"
              className="group inline-flex items-center justify-center gap-3 bg-[#22C55E] hover:bg-[#16A34A] text-black font-bold uppercase tracking-wide rounded-full px-10 py-5 text-lg transition-all duration-300 hover:-translate-y-1 shadow-lg shadow-[#22C55E]/25"
            >
              Apply For The £5K Guarantee
              <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </section>
      </main>

      <ApexFooter />
    </div>
  );
};

export default FiveKGuarantee;
